import React, { createContext, useContext, useRef, useCallback } from 'react';

const MAX_SEEN_IDS = 50;
const SEEN_IDS_STORAGE_KEY = 'reels-feed-seen-ids';

function readSeenIdsFromStorage(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SEEN_IDS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string' && v.length > 0));
  } catch {
    return new Set();
  }
}

function writeSeenIdsToStorage(ids: Set<string>): void {
  try {
    sessionStorage.setItem(SEEN_IDS_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // Ignore storage failures; in-memory behavior still works.
  }
}

interface ReelsFeedContextValue {
  /** Get current excludeIds for feed requests. Persists across re-renders and refetch. */
  getExcludeIds: () => string[];
  /** Mark a reel id as seen; used for excludeIds on subsequent requests. */
  addSeenId: (id: string) => void;
}

const ReelsFeedContext = createContext<ReelsFeedContextValue | undefined>(undefined);

export function ReelsFeedProvider({ children }: { children: React.ReactNode }) {
  const seenIdsRef = useRef<Set<string>>(readSeenIdsFromStorage());

  const addSeenId = useCallback((id: string) => {
    if (!id) return;
    seenIdsRef.current.add(id);
    const set = seenIdsRef.current;
    if (set.size > MAX_SEEN_IDS) {
      const arr = Array.from(set);
      arr.slice(0, set.size - MAX_SEEN_IDS).forEach((x) => set.delete(x));
    }
    writeSeenIdsToStorage(set);
  }, []);

  const getExcludeIds = useCallback(() => Array.from(seenIdsRef.current), []);

  const value: ReelsFeedContextValue = {
    getExcludeIds,
    addSeenId,
  };

  return (
    <ReelsFeedContext.Provider value={value}>
      {children}
    </ReelsFeedContext.Provider>
  );
}

export function useReelsFeedContext(): ReelsFeedContextValue {
  const ctx = useContext(ReelsFeedContext);
  if (ctx === undefined) {
    throw new Error('useReelsFeedContext yalnızca ReelsFeedProvider içinde kullanılabilir.');
  }
  return ctx;
}
